/**
 * Workspace chooser (11-Authentication/Drafts/04-workspace-chooser-light-v5.png).
 * The directory is the caller's complete membership list (≤1,000), so search, tabs
 * and sorting are full-result operations. Pending requests are open invitations
 * addressed to the user's confirmed email; joining still needs the emailed link.
 */
import { retryRead } from '@miraops/contracts';
import { useEffect, useMemo, useState } from 'react';
import { ArrowUpDown, ChevronDown, CirclePlus, LogOut, Search, UserPlus } from '../ui/glyphs.tsx';
import {
  arrangeWorkspaces, classifyFailure, initials, listMyInvitations, listMyWorkspaces, recentIds,
  type Account, type Failure, type Invitation, type Workspace, type WorkspaceFilter, type WorkspaceSort,
} from '@miraops/contracts';
import { listMyPortals } from '@miraops/contracts';
import { transport } from '../lib/session.ts';
import { useI18n, type I18n } from '../lib/i18n.tsx';
import type { Prefs } from '../lib/prefs.ts';
import { Avatar, Banner, Button, Dropdown, EmptyState, Segmented, Spinner, useDismiss } from '../ui/ui.tsx';
import { Wordmark } from '../ui/icons.tsx';
import { HelpLink, LegalLinks, PrefsCluster } from '../auth/PublicChrome.tsx';
import {failureText} from '../lib/failure-text.ts';
import { quietly } from '../lib/quietly.ts';

export function lastOpenedLabel(i18n: I18n, at: number | null, now = new Date()): string {
  if (at === null) return i18n.t('ws.never');
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(now) - day(new Date(at))) / 86400000);
  if (diff <= 0) return i18n.t('ws.today', { time: i18n.time.format(at) });
  if (diff === 1) return i18n.t('ws.yesterday');
  return i18n.shortDate.format(at);
}

export function AccountButton({ account, email, onSignOut, extra }: { account: Account | null; email: string; onSignOut: () => void; extra?: React.ReactNode }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const name = account?.displayName || email;
  return (
    <div className="dropdown" ref={ref}>
      <button type="button" className="account-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <Avatar text={initials(name)} seed={email} tone="blue" size="lg" />
        <span className="who"><strong>{name}</strong><span>{email}</span></span>
        <ChevronDown size={20} className="muted" />
      </button>
      {open && (
        <div className="menu menu-end" role="menu">
          {extra}
          <button type="button" role="menuitem" onClick={onSignOut}><LogOut size={16} />{t('auth.signOut')}</button>
        </div>
      )}
    </div>
  );
}

export function WorkspacePicker({ account, email, prefs, onPrefs, onOpen, onOpenPortal, onCreate, onSignOut, notice }: {
  account: Account | null; email: string; prefs: Prefs; onPrefs: (p: Prefs) => void;
  onOpen: (w: Workspace) => void; onOpenPortal: (invitationId: string) => void; onCreate: () => void; onSignOut: () => void; notice?: string;
}) {
  const [portals, setPortals] = useState<Awaited<ReturnType<typeof listMyPortals>>>([]);
  useEffect(() => { retryRead(() => listMyPortals(transport)).then(setPortals).catch(quietly); }, []);
  const i18n = useI18n();
  const { t, collator } = i18n;
  const [list, setList] = useState<Workspace[] | null>(null);
  const [invites, setInvites] = useState<Invitation[] | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  // Invitations that couldn't be listed are said, not shown as none.
  const [inviteFailure, setInviteFailure] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<WorkspaceSort>('recent');
  const [filter, setFilter] = useState<WorkspaceFilter>('all');
  const [attempt, setAttempt] = useState(0);
  const [joinHint, setJoinHint] = useState(false);

  useEffect(() => {
    const abort = new AbortController();
    setFailure(null); setInviteFailure(null);
    listMyWorkspaces(transport, abort.signal).then(setList)
      .catch(error => { if (!abort.signal.aborted) setFailure(classifyFailure({ error, operation: 'read' })); });
    listMyInvitations(transport, abort.signal).then(setInvites).catch(error => { if (!abort.signal.aborted) { setInvites([]); setInviteFailure(failureText(t, classifyFailure({ error, operation: 'read' }))); } });
    return () => abort.abort();
  }, [attempt]);

  const arranged = useMemo(() => list ? arrangeWorkspaces(list, search, filter, sort, collator) : [], [list, search, filter, sort, collator]);
  const recent = useMemo(() => list ? recentIds(list) : new Set<string>(), [list]);
  const pending = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return (invites ?? []).filter(i => !needle || i.workspaceName.toLocaleLowerCase().includes(needle))
      .sort((a, b) => sort === 'name' ? collator.compare(a.workspaceName, b.workspaceName) : sort === 'role' ? collator.compare(a.roleName, b.roleName) : b.createdAt - a.createdAt);
  }, [invites, search, sort, collator]);
  const availableCount = list?.filter(w => w.available).length ?? 0;
  const shown = filter === 'pending' ? pending.length : arranged.length;

  return (
    <div className="public">
      <header className="chooser-head">
        <Wordmark size="lg" />
        <AccountButton account={account} email={email} onSignOut={onSignOut} />
      </header>
      <main className="chooser" aria-labelledby="picker-title">
        <div className="chooser-body">
          <h1 id="picker-title">{t('ws.title')}</h1>
          {notice && <div style={{ padding: '12px 56px 0' }}><Banner tone="attention">{notice}</Banner></div>}
          {inviteFailure && !failure && <div style={{ padding: '12px 56px 0' }}><Banner tone="attention">{inviteFailure}</Banner></div>}
          <div className="chooser-tools">
            <div className="search-box">
              <Search size={22} strokeWidth={1.8} />
              <input type="search" className="field-input" placeholder={t('ws.search')} aria-label={t('ws.search')} value={search} onChange={e => setSearch(e.target.value)} autoFocus />
            </div>
            <Segmented<WorkspaceFilter> label={t('ws.title')} value={filter} onChange={setFilter} options={[
              { value: 'all', label: `${t('ws.filter.all')} (${list?.length ?? '…'})` },
              { value: 'available', label: `${t('ws.filter.available')} (${list ? availableCount : '…'})` },
              { value: 'pending', label: `${t('ws.filter.pending')} (${invites?.length ?? '…'})` }]} />
            <Dropdown<WorkspaceSort> className="sort" align="end" label={t('ws.sort.lastOpened')} value={sort} onChange={setSort}
              icon={<ArrowUpDown size={20} strokeWidth={1.8} />}
              options={[{ value: 'recent', label: t('ws.sort.lastOpened') }, { value: 'name', label: t('ws.sort.nameAZ') }, { value: 'role', label: t('ws.sort.yourRole') }]} />
          </div>
          {failure && <div style={{ padding: '0 56px' }}><Banner tone="danger" action={<Button onClick={() => setAttempt(a => a + 1)}>{t('state.retry')}</Button>}>{failureText(t,failure)}</Banner></div>}
          <div className="ws-table" role="table" aria-label={t('ws.title')}>
            <div className="ws-grid head" role="row">
              <div role="columnheader">{t('ws.col.workspace')}</div>
              <div role="columnheader">{t('ws.col.role')}</div>
              <div role="columnheader">{t('ws.col.access')}</div>
              <div role="columnheader">{t('ws.col.lastOpened')}</div>
              <div role="columnheader"><span className="visually-hidden">{t('ws.open')}</span></div>
            </div>
            {!list && !failure && <div className="center pad"><Spinner label={t('state.loading')} /></div>}
            {filter !== 'pending' && list && list.length === 0 && <EmptyState title={t('ws.empty')} />}
            {filter !== 'pending' && list && list.length > 0 && arranged.length === 0 && <EmptyState title={t('ws.noMatch')} />}
            {filter !== 'pending' && arranged.map(w => (
              <div key={w.id} role="row" className={`ws-grid ws-row${w.available ? '' : ' unavailable'}`}>
                <div role="cell">
                  <Avatar text={initials(w.name)} seed={w.id} />
                  <span className="ws-name">{w.name}</span>
                  {recent.has(w.id) && <span className="ws-recent">{t('ws.recent')}</span>}
                </div>
                <div role="cell">{w.role}</div>
                <div role="cell"><span className={`access-dot${w.available ? '' : ' warn'}`} aria-hidden />{w.unavailableReason ? t(`ws.reason.${w.unavailableReason}`) : t('ws.access.available')}</div>
                <div role="cell" className="muted">{lastOpenedLabel(i18n, w.lastOpenedAt)}</div>
                <div role="cell" className="open-cell">
                  <Button disabled={!w.available} onClick={() => onOpen(w)} aria-label={`${t('ws.open')} ${w.name}`}>{t('ws.open')}</Button>
                </div>
              </div>
            ))}
            {filter === 'pending' && invites && pending.length === 0 && <EmptyState title={t('ws.noPending', { email })} />}
            {filter === 'pending' && pending.map(i => (
              <div key={i.id} role="row" className="ws-grid ws-row">
                <div role="cell"><Avatar text={initials(i.workspaceName)} seed={i.workspaceId} /><span className="ws-name">{i.workspaceName}</span></div>
                <div role="cell">{i.roleName}</div>
                <div role="cell"><span className="access-dot warn" aria-hidden />{t('ws.invited')}</div>
                <div role="cell" className="muted">{t('ws.expires', { date: i18n.shortDate.format(i.expiresAt) })}</div>
                <div role="cell" className="open-cell muted small" style={{ whiteSpace: 'normal', textAlign: 'end' }}>{t('ws.waitingLink')}</div>
              </div>
            ))}
          </div>
          {portals.length > 0 && <div className="portal-list">
            <h2>{t('portal.sharedWithYou')}</h2>
            {portals.map(p => <button key={p.invitation_id} type="button" className="portal-entry" onClick={() => onOpenPortal(p.invitation_id)}>
              <strong>{t('portal.grantedBy', { workspace: p.workspace_name })}</strong><span className="muted small">{t('portal.for', { company: p.company_name })} · {t('portal.nShipments', { n: p.orders })}</span></button>)}
          </div>}
          <p className="chooser-count">{filter === 'pending' ? t('ws.pendingCount', { n: shown }) : t('ws.count', { n: shown })}</p>
          <div className="chooser-actions">
            <Button icon={<CirclePlus size={24} strokeWidth={1.6} />} onClick={onCreate}>{t('ws.create')}</Button>
            <Button icon={<UserPlus size={24} strokeWidth={1.6} />} onClick={() => setJoinHint(h => !h)} aria-expanded={joinHint}>{t('ws.join')}</Button>
          </div>
          {joinHint && <p className="center muted" role="status" style={{ marginTop: -12, paddingBottom: 12 }}>{t('ws.joinHint')}</p>}
        </div>
      </main>
      <footer className="public-foot">
        <HelpLink />
        <span className="spacer" />
        <LegalLinks />
        <span className="spacer" />
        <PrefsCluster prefs={prefs} onPrefs={onPrefs} />
      </footer>
    </div>
  );
}
